if (!customElements.get('collection-infinite-scroll')) {
  customElements.define('collection-infinite-scroll', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) this.initialize();
      });
    }

    initialize() {
      this.container = this.closest('#ProductGridContainer');
      this.grid = this.container?.querySelector('ul#product-grid');
      this.link = this.querySelector('[data-load-more]');
      this.status = this.querySelector('[data-load-status]');
      if (!this.grid || !this.link || !this.status) return;
      this.queryToken = this.getQueryToken();
      this.nextUrl = this.dataset.nextUrl;
      this.pending = false;
      this.paused = false;
      this.seenPages = new Set();
      this.onClick = (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        this.paused = false;
        this.loadMore(event.detail === 0);
      };
      this.link.addEventListener('click', this.onClick);
      const pagination = this.container.querySelector('[data-collection-pagination]');
      if (pagination) pagination.hidden = true;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadMore(false);
        }, { rootMargin: '500px 0px' });
      }
      this.update();
      this.observe();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.link?.removeEventListener('click', this.onClick);
    }

    getQueryToken() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.sort();
      return url.pathname + url.search;
    }

    isCurrent() {
      return this.isConnected && this.queryToken === this.getQueryToken()
        && !this.container.querySelector('.collection.loading');
    }

    observe() {
      if (this.nextUrl && !this.paused && this.isCurrent()) this.observer?.observe(this);
    }

    update() {
      this.link.hidden = !this.nextUrl;
      if (this.nextUrl) this.link.href = this.nextUrl;
      this.dataset.nextUrl = this.nextUrl || '';
      const count = this.grid.children.length;
      this.status.textContent = this.nextUrl
        ? this.dataset.loadedMessage.replace('[count]', String(count))
        : this.dataset.completeMessage;
    }

    async loadMore(focusNewProduct = false) {
      if (this.pending || this.paused || !this.nextUrl || !this.isCurrent()) return;
      this.pending = true;
      this.observer?.disconnect();
      this.status.textContent = this.dataset.loadingMessage;
      this.link.setAttribute('aria-disabled', 'true');
      this.grid.setAttribute('aria-busy', 'true');
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        const url = new URL(this.nextUrl, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) throw new Error('Invalid collection URL');
        url.hash = '';
        url.searchParams.set('section_id', this.dataset.sectionId);
        if (this.seenPages.has(url.href)) throw new Error('Repeated collection page');
        const response = await fetch(url.href, {
          signal: this.controller.signal,
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Collection request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isCurrent()) return;
        const sourceGrid = html.querySelector('ul#product-grid');
        const sourceLoader = html.querySelector('collection-infinite-scroll');
        if (!sourceGrid || !sourceLoader || sourceLoader.dataset.sectionId !== this.dataset.sectionId) throw new Error('Missing collection content');
        const nextUrl = sourceLoader.dataset.nextUrl || '';
        const loadedIds = new Set(Array.from(this.grid.children, (item) => item.dataset.productId));
        const items = Array.from(sourceGrid.children).filter((item) => {
          if (!item.dataset.productId || loadedIds.has(item.dataset.productId)) return false;
          loadedIds.add(item.dataset.productId);
          return true;
        }).map((item) => document.importNode(item, true));
        items.forEach((item) => {
          if (item.classList.contains('scroll-trigger')) item.classList.add('scroll-trigger--cancel');
          item.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
          item.querySelectorAll('img').forEach((image) => { image.loading = 'lazy'; });
        });
        this.grid.append(...items);
        this.seenPages.add(url.href);
        this.nextUrl = nextUrl;
        this.update();
        if (focusNewProduct && items.length) items[0].querySelector('a[href]')?.focus();
      } catch (error) {
        if (this.isCurrent()) {
          this.paused = true;
          this.status.textContent = this.dataset.errorMessage;
        }
      } finally {
        clearTimeout(timeout);
        this.pending = false;
        this.link.removeAttribute('aria-disabled');
        this.grid.removeAttribute('aria-busy');
        this.observe();
      }
    }
  });
}
